import React, {Fragment, PureComponent, ReactNode} from 'react';
import {artifactStore, userStore} from 'app/store/global';
import {modalConfirm} from 'app/utils/modals/popups';
import {showPopup} from 'app/utils/modals/ModalsService';
import I18n from 'app/i18n';
import SendArtifactPopup from './send_artifact_popup';
import SellArtifactPopup from './sell_artifact_popup';
import {ajaxError, localDateTimeFromISO} from 'app/utils';
import {goToRoute} from 'app/utils/link_helper';
import InventoryItemDescription from 'app/components/my_profile/artifacts/popup/inventory_item_description';
import Smiles from 'app/components/my_profile/artifacts/popup/smiles';
import ActionButton from './action_button';
import {artifactShopLink} from 'app/components/my_profile/artifacts/utils';
import Notice from './popup/notice';
import {firstElementResponse} from 'app/utils/lootbox_open/first_element_response';
import PrizeCard from 'app/components/reusable/PrizeCard';
import LootboxDetailsCarousel, {
    INVENTORY_LOOTBOX_SPIN_DELAY_MS,
    lootboxCarouselResultDelayMs
} from 'app/components/shop/lootboxes/LootboxDetailsCarousel';
import LootboxDetailsPrizes from 'app/components/shop/lootboxes/LootboxDetailsPrizes';
import SmartLoader from 'app/components/reusable/SmartLoader';
import {inject, observer} from 'mobx-react';
import {coolOffLootbox} from 'app/utils/isCoolOffActiveFor';
import {isLootBox} from 'app/utils/isLootBox';
import {ShopLootboxStore} from 'app/store/ShopLootboxStore';
import {UserStore} from 'app/store/UserStore';
import InventoryCommonPropertiesInterface from 'app/interfaces/InventoryCommonPropertiesInterface';
import InventoryItemInterface from 'app/interfaces/InventoryItemInterface';
import LootInterface from 'app/interfaces/LootInterface';
import SteamSkinInterface from 'app/interfaces/SteamSkinInterface';
import InventoryItemsEffectInterface from 'app/interfaces/InventoryItemsEffectInterface';
import {isDesktopSmall} from 'app/utils/windowFunctions';

interface ResponseJSONInterface {
    extra_data: LootInterface[];
    inventory_items: InventoryItemInterface[];
    inventory_items_effects: InventoryItemsEffectInterface[];
    steam_skin: SteamSkinInterface;
    steam_trade: SteamSkinInterface;
    win_artifact: InventoryItemInterface;
}

interface Props {
    artifact: InventoryItemInterface;
    commonProperties: InventoryCommonPropertiesInterface;
    close: () => void;
    propsUpdateState: (state: {
        contentMinHeight?: number | null;
        openingLootbox?: boolean;
        responseJSON?: ResponseJSONInterface;
    }) => void;
    shopLootboxStore?: ShopLootboxStore;
    userStore?: UserStore;
}

interface State {
    loading: boolean;
    openingFadeOut: boolean;
}

const OPENING_FADE_OUT_MS = 900;

class InventoryPopupContent extends PureComponent<Props, State> {
    state = {
        loading: false,
        openingFadeOut: false
    };

    openStartedAt = 0;

    fadeTimer: ReturnType<typeof setTimeout> | null = null;

    resultTimer: ReturnType<typeof setTimeout> | null = null;

    componentDidMount(): void {
        this.toggleAutoOpeningLootbox(true);
    }

    componentWillUnmount(): void {
        const {commonProperties: {onCloseInventoryPopup, openingLootbox}, shopLootboxStore, artifact} = this.props;

        this.clearTimers();

        if (!this.isArtifact) {
            onCloseInventoryPopup();
        }
        if (openingLootbox && artifact && isLootBox(artifact.item_type)) {
            shopLootboxStore?.updateLootboxOpeningInProgress(false);
        }
        this.toggleAutoOpeningLootbox(false);
    }

    get isUseButtonDisabled() {
        const {artifact: {useable}} = this.props;
        return !useable;
    }

    get isSendButtonDisabled() {
        const {restrict_artifact} = userStore.user;
        const {artifact: {sending_prohibited, transferable}} = this.props;
        return sending_prohibited || !transferable || restrict_artifact;
    }

    get isSellButtonDisabled() {
        const {restrict_artifact} = userStore.user;
        const {artifact: {salable}} = this.props;
        return !salable || restrict_artifact;
    }

    get publicView() {
        const {artifact: {public_view}} = this.props;
        return public_view;
    }

    get isArtifact() {
        const {commonProperties: {isArtifact}} = this.props;
        return isArtifact;
    }

    messagePromoCodeActiveAt() {
        const {artifact: {is_promo_code_active, promo_code_active_at}} = this.props;

        if (typeof is_promo_code_active === 'undefined' || is_promo_code_active) {
            return null;
        }
        return <p>
            {I18n.t('user.promo_code_will_be_able_to_use_after',
                {ending_date: localDateTimeFromISO(promo_code_active_at)})}
        </p>;
    }

    useButtonText = () => {
        const {artifact: {equipped}} = this.props;

        if (this.isArtifact) {
            return I18n.t(equipped
                ? 'unequip'
                : 'equip');
        }

        return I18n.t('open');
    };

    clearTimers = (): void => {
        if (this.fadeTimer) {
            clearTimeout(this.fadeTimer);
            this.fadeTimer = null;
        }

        if (this.resultTimer) {
            clearTimeout(this.resultTimer);
            this.resultTimer = null;
        }
    };

    popupRef = React.createRef<HTMLDivElement>();

    startLootboxOpening = (): void => {
        this.openStartedAt = Date.now();
        this.props.shopLootboxStore?.updateLootboxOpeningInProgress(true);
        this.setState({openingFadeOut: false});
        this.updateOuterState({
            contentMinHeight: this.popupContentHeight(),
            openingLootbox: true
        });
    };

    popupContentHeight = (): number => {
        const popup = this.popupRef.current;
        const content = popup?.closest('.popup__content');

        return content instanceof HTMLElement
            ? content.offsetHeight
            : popup?.offsetHeight || 0;
    };

    stopLootboxOpening = (): void => {
        this.props.shopLootboxStore?.updateLootboxOpeningInProgress(false);
    };

    schedulePrizeResult = (response: ResponseJSONInterface): void => {
        const {handleAnimationEnded} = this.props.commonProperties;

        this.updateOuterState({responseJSON: response});
        this.clearTimers();

        const spinDoneDelay = lootboxCarouselResultDelayMs(this.openStartedAt, INVENTORY_LOOTBOX_SPIN_DELAY_MS);
        const mobile = !isDesktopSmall();
        const prizeDelay = mobile
            ? spinDoneDelay + OPENING_FADE_OUT_MS
            : spinDoneDelay;

        if (mobile) {
            this.fadeTimer = setTimeout(() => {
                this.setState({openingFadeOut: true});
            }, spinDoneDelay);
        }

        this.resultTimer = setTimeout(() => {
            this.stopLootboxOpening();
            handleAnimationEnded();
        }, prizeDelay);
    };

    use = ({continue_equipping = false}): void => {
        if (this.publicView) {
            return;
        }

        const {artifact} = this.props;
        const isLootbox = isLootBox(artifact.item_type);

        if (isLootbox) {
            this.startLootboxOpening();
        }

        artifactStore
            .use(artifact, continue_equipping, false)
            .fail((response: {responseJSON: []}) => {
                if (isLootbox) {
                    this.stopLootboxOpening();
                    this.setState({openingFadeOut: false});
                    this.updateOuterState({
                        contentMinHeight: null,
                        openingLootbox: false
                    });
                    ajaxError(response);
                    return;
                }
                modalConfirm(firstElementResponse(response),
                    () => this.use({continue_equipping: true}));
            })
            .done((response: ResponseJSONInterface) => {
                if (this.isArtifact) {
                    this.props.close();
                    this.setState({loading: false});
                    return;
                }

                this.schedulePrizeResult(response);
                this.setState({loading: false});
            });
    };

    updateOuterState = (state: {
        contentMinHeight?: number | null;
        openingLootbox?: boolean;
        responseJSON?: ResponseJSONInterface;
    }): void => {
        this.props.propsUpdateState(state);
    };

    send = () => {
        const {artifact} = this.props;
        const {sending_prohibited, public_view} = artifact;

        if (sending_prohibited || public_view) {
            return;
        }

        showPopup('inventory-popup', props => <SendArtifactPopup artifact={artifact} popupId={props.popupId}/>);
    };

    sell = () => {
        const {artifact} = this.props;

        if (this.publicView) {
            return;
        }

        if (artifact.in_market) {
            this.props.close();
            goToRoute(artifactShopLink(artifact.recipe, true), true);
        } else {
            showPopup(
                'sell-inventory',
                props => <SellArtifactPopup artifact={artifact} popupId={props.popupId}/>
            );
        }
    };

    toggleAutoOpeningLootbox = (open = false) => {
        if (window.location.pathname.includes('hero')) {
            const {shopLootboxStore} = this.props;

            if (shopLootboxStore?.openNowLootbox && open) {
                this.use({});
            }
            shopLootboxStore?.updateOpenNowStatus(false);
        }
    };

    coolOffLootboxesDisabled = () => {
        const {artifact} = this.props;

        return isLootBox(artifact.item_type) && coolOffLootbox(userStore.user);
    };


    renderLootboxPrizes = (): ReactNode => {
        const {artifact} = this.props;
        return <LootboxDetailsPrizes loot={artifact.loot || []} hideName/>;
    };

    renderOpeningCarousel = (): ReactNode => {
        const {artifact, commonProperties: {extra_data}} = this.props;
        const winner = extra_data?.[0] || null;

        return <div className="inventory__popup--carousel-stage lootbox-detail">
            <div className="lootbox-detail__opening-stage">
                <LootboxDetailsCarousel
                    fading={this.state.openingFadeOut}
                    loot={artifact.loot || []}
                    spinDelayMs={INVENTORY_LOOTBOX_SPIN_DELAY_MS}
                    winner={winner}
                />
            </div>
        </div>;
    };

    render(): ReactNode {
        const {commonProperties, artifact} = this.props;
        const {openingLootbox, useNow, isLootbox} = commonProperties;
        const {in_market} = artifact;
        const {loading, openingFadeOut} = this.state;
        const opening = Boolean(openingLootbox && isLootbox);
        const popupClassName = [
            'inventory__popup',
            opening ? 'inventory__popup--opening' : '',
            openingFadeOut ? 'inventory__popup--opening-out' : ''
        ].filter(Boolean).join(' ');

        return <div className={popupClassName} ref={this.popupRef}>
            <div className="inventory__popup--slide">
                <div className="inventory__popup--slide-inner">
                    <div className="inventory__popup--description">
                        <div className="inventory__popup--img-wrap">
                            <div
                                className={`market-details--info--img inventory__popup--prize-preview${
                                    isLootbox ? ' inventory__popup--prize-preview-lootbox' : ''
                                }`}
                            >
                                <PrizeCard
                                    artifact={artifact}
                                    hideMeta
                                    hideName
                                    quantity={artifact.total_inventory_items}
                                    showQuantityOrange
                                />
                            </div>
                        </div>
                        <div className="inventory__popup--open-hide">
                            <div className="inventory__popup--open-hide-inner">
                                <div className="inventory__popup--desc">
                                    {isLootbox
                                        ? <Fragment>
                                            <div className="inventory__popup--desc-title">
                                                {I18n.t('you_may_win')}
                                            </div>
                                            {this.renderLootboxPrizes()}
                                        </Fragment>
                                        : <InventoryItemDescription
                                            isLootbox={false}
                                            item_type={artifact.item_type}
                                            loot={artifact.loot}
                                            description={artifact.description}
                                            description_langs={artifact.description_langs}
                                        />}
                                    {this.messagePromoCodeActiveAt()}
                                    <Smiles {...artifact}/>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="inventory__popup--open-hide">
                        <div className="inventory__popup--open-hide-inner">
                            {loading
                                ? <SmartLoader loaderClassName="smart-loader_theme-dark_no-margin" />
                                : <div className="inventory__popup--actions">
                                    {!useNow && <Fragment>
                                        <ActionButton
                                            {...artifact}
                                            disabled={this.isUseButtonDisabled || this.coolOffLootboxesDisabled() || opening}
                                            onClickHandler={this.use}
                                            btnText={this.useButtonText()}
                                        />
                                        <ActionButton
                                            {...artifact}
                                            disabled={this.isSendButtonDisabled || opening}
                                            onClickHandler={this.send}
                                            btnText={I18n.t('send_as_gift')}
                                        />
                                        <ActionButton
                                            {...artifact}
                                            disabled={this.isSellButtonDisabled || opening}
                                            onClickHandler={this.sell}
                                            btnText={in_market ? I18n.t('show_now') : I18n.t('sell')}
                                        />
                                    </Fragment>}
                                </div>}
                            <Notice {...artifact} openingLootbox={openingLootbox}/>
                        </div>
                    </div>
                </div>
            </div>
            {opening && this.renderOpeningCarousel()}
        </div>;
    }
}

export default inject('shopLootboxStore', 'userStore')(observer(InventoryPopupContent));
